import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons"
import { Button, Card, Dropdown, Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { AlbumListItem } from "../../../../../../Album/types/album.types.ts"
import { AlbumThumbnails } from "./AlbumThumbnails.tsx"

const { Text } = Typography

interface AlbumCardProps {
  album: AlbumListItem
  onView?: (id: string) => void
  userId: string
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  onDeleteClick: (albumId: string) => void
  editAllowed: boolean
  onEditClick: (albumId: string) => void
}

export const AlbumCard = ({
  album,
  onView,
  userId,
  deleteAllowed,
  deleteForeignAllowed,
  onDeleteClick,
  editAllowed,
  onEditClick
}: AlbumCardProps) => {
  const { t } = useTranslation()
  const count = album?.attachments_count || 0
  const created = album?.created
  const deleteAlbumAllowed =
    deleteForeignAllowed || (deleteAllowed && userId === album?.created_by)

  const onViewHandler = () => {
    onView?.(album?.id)
  }

  return (
    <Card
      onClick={onViewHandler}
      hoverable
      className="h-full cursor-pointer"
      styles={{ body: { padding: 0 } }}
    >
      <AlbumThumbnails attachments={album?.attachments} />

      <div className="p-2.5">
        <Flex justify="space-between" align="center" gap={4}>
          <Text
            ellipsis={{ tooltip: album?.name }}
            strong
            className="min-w-0 flex-1"
          >
            {album?.name}
          </Text>

          {(editAllowed || deleteAlbumAllowed) && (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  ...(editAllowed
                    ? [
                        {
                          key: "edit",
                          label: t("files.albums.actions.edit"),
                          icon: <EditOutlined />
                        }
                      ]
                    : []),
                  ...(deleteAlbumAllowed
                    ? [
                        {
                          key: "delete",
                          label: t("files.albums.actions.delete"),
                          icon: <DeleteOutlined />,
                          danger: true
                        }
                      ]
                    : [])
                ],
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation()
                  if (key === "edit") onEditClick(album.id)
                  else if (key === "delete") onDeleteClick(album.id)
                }
              }}
            >
              <Button
                type="text"
                size="small"
                className="shrink-0"
                icon={<MoreOutlined />}
                aria-label={t("files.albums.actions.menu", {
                  name: album?.name
                })}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          )}
        </Flex>

        <Text type="secondary" className="text-xs">
          {created} • {t("files.albums.fileCount", { count })}
        </Text>
      </div>
    </Card>
  )
}
